import { Link } from 'react-router-dom';
import { Chip, ZebraTable, type ChipProps } from '@kawai/ui';
import type {
  BillingCharge,
  TenantBillingStatus,
  TenantFeature,
  TenantStatus,
} from '@kawai/shared';
import {
  bankTransferDisplayStatusLabel,
  billingChargeKindLabel,
  billingChargeStatusLabel,
  formatCardExpiry,
  formatCardLabel,
  formatYen,
  isOfflinePaymentMethod,
  resolveBankTransferDisplayStatus,
  todayJst,
} from '@kawai/shared';
import { useAuth } from '../lib/auth.js';
import { getFeatureUrl } from '../lib/featureUrls.js';
import { useTenantBrandContext } from '../lib/tenantBrand.js';
import { useBillingCharges } from '../lib/hooks/useBillingCharges.js';
import { MfaSettings } from '@kawai/auth-bootstrap';
import { ImpersonationBanner } from '../components/ImpersonationBanner.js';
import { ROUTES } from '../routes.js';

/** 認証アプリ（TOTP）に表示される発行者名。S-75。 */
const MFA_ISSUER = 'TracePit ポータル';

/** S-90: 契約情報カードに表示する機能パッケージの日本語ラベル。 */
const FEATURE_LABELS: Record<TenantFeature, string> = {
  reservation: 'LINE予約',
  fuel: '燃料価格',
  richmenu: 'リッチメニュー',
  promo: '顧客/販促',
};

/** S-90: テナント向けの契約状態ラベル（saas-admin は raw 値表示だが、テナントには日本語で出す）。 */
const TENANT_STATUS_LABELS: Record<TenantStatus, string> = {
  active: '利用中',
  suspended: '停止中',
  archived: '契約終了',
};

type ChipTone = NonNullable<ChipProps['tone']>;

function tenantStatusTone(status: TenantStatus): ChipTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warning';
    case 'archived':
      return 'neutral';
  }
}

/**
 * S-90: テナント向けの請求ライフサイクル状態ラベル。
 * saas-admin（TP-25）は運営者向けに raw 値を badge 表示するが、テナントには
 * 決済フローの段階が分かる日本語で出す。
 */
const BILLING_STATUS_LABELS: Record<TenantBillingStatus, string> = {
  none: '未開始',
  card_pending: 'カード登録待ち',
  card_registered: 'カード登録済み',
  initial_charging: '初期費用 決済処理中',
  initial_paid: '初期費用 支払い済み',
  initial_failed: '初期費用 決済失敗',
  subscription_starting: '月額プラン開始処理中',
  active: '月額プラン利用中',
  past_due: '支払い遅延',
  canceled: '解約済み',
  suspended: '停止中',
};

/** 悪い側（遅延 / 失敗 / 停止）は面を作らず warning の面 + 文字で示す（v2 の状態色）。 */
function billingStatusTone(status: TenantBillingStatus): ChipTone {
  switch (status) {
    case 'active':
    case 'initial_paid':
      return 'success';
    case 'past_due':
    case 'initial_failed':
    case 'suspended':
      return 'warning';
    case 'canceled':
      return 'neutral';
    default:
      return 'brand';
  }
}

/** 請求 1 行の結果ラベル。振込・口座振替は表示派生ステータス（TP-27/TP-34）、カードは従来ラベル。 */
function chargeStatusLabel(charge: BillingCharge): string {
  if (isOfflinePaymentMethod(charge.paymentMethod)) {
    return bankTransferDisplayStatusLabel(
      resolveBankTransferDisplayStatus(charge.status, charge.dueDate, todayJst()),
      charge.paymentMethod,
    );
  }
  return billingChargeStatusLabel(charge.status);
}

interface SummaryItemProps {
  label: string;
  children: React.ReactNode;
}

function SummaryItem({ label, children }: SummaryItemProps) {
  return (
    <div>
      <dt className="text-label font-medium text-ink-2">{label}</dt>
      <dd className="mt-1 text-body text-ink">{children}</dd>
    </div>
  );
}

/**
 * S-90: ポータルのホーム（テナント情報ダッシュボード）。
 *
 * 旧・機能カードランチャー（FEATURE_CARDS + SSO handover）は廃止し、各管理画面への
 * 遷移は AppShell のヘッダータブ（Layout / resolveManagementAppLinks）に一本化した。
 * richmenu の「準備中」カードも廃止（ready になったら MANAGEMENT_APP_LINKS へ追加すれば
 * 全アプリのタブに現れる）。
 *
 * 認可ガード（loading / signed-out / saas-admin 純運営者転送）は Layout に集約済み。
 * 本ページは signed-in かつテナント所属（または impersonation）前提で描画される。
 *
 * S-136: 見た目は docs/UI-GUIDE.md の v2（白カード 30/26・状態は Chip・表は ZebraTable）。
 * カード見出しのテナント色はやめて本文黒に統一（ブランド色は上部メニューが担う）。
 */

const CARD = 'rounded-xl bg-white px-[30px] py-[26px]';
const CARD_TITLE = 'text-h-card font-semibold text-ink';
const NOTE = 'text-body text-ink-3';
export function HomePage() {
  const {
    role,
    features,
    email,
    tenantId,
    signOut,
    user,
    mfaEnrolled,
    refreshClaims,
    impersonatedBy,
    impersonationExpiresAt,
  } = useAuth();
  // tenants doc は Layout が 1 回だけ fetch し context で共有する（read 二重化防止）。
  const { status: tenantStatus, brand, tenant } = useTenantBrandContext();

  // お支払いサマリーは従来の「お支払い設定」カードと同じ条件（staff には出さない）。
  const canViewBilling = !!tenantId && (role === 'tenant-admin' || role === 'saas-admin');
  const { status: chargesStatus, charges } = useBillingCharges(
    canViewBilling ? tenantId : null,
  );

  // S-07: 「saas-admin に戻る」ボタン:
  //   1. impersonation セッションを抹消するため signOut
  //   2. saas-admin から `window.open(_blank, 'noopener')` で開かれているので、
  //      `window.close()` でこのタブを閉じる (= saas-admin タブに自然復帰)
  //   3. close が拒否される (ユーザーが直接 URL 入力で開いた等) 場合は
  //      saas-admin URL に navigate して fallback
  async function handleReturnToSaasAdmin() {
    try {
      await signOut();
    } finally {
      try {
        window.close();
      } catch {
        // noop
      }
      // close 拒否時のフォールバック (close 後に到達することは無いはずだが、
      // 同期的に閉じない実装に対する保険)
      window.location.href = getFeatureUrl('saas-admin');
    }
  }

  const billingStatus: TenantBillingStatus = tenant?.billingStatus ?? 'none';

  return (
    <div className="mx-auto max-w-5xl">
      {impersonatedBy && (
        <ImpersonationBanner
          impersonatedBy={impersonatedBy}
          expiresAt={impersonationExpiresAt}
          returnTo={getFeatureUrl('saas-admin')}
          onReturn={() => {
            void handleReturnToSaasAdmin();
          }}
        />
      )}

      <header>
        <h1 className="text-h-page font-semibold text-ink">ホーム</h1>
        <p className="mt-1 text-body text-ink-2">
          {impersonatedBy
            ? `${email ?? 'impersonation'} (閲覧専用)`
            : email
              ? `${email} としてサインイン中`
              : 'サインイン中'}
        </p>
      </header>

      <section aria-label="契約情報" className="mt-6">
        <article className={CARD}>
          <h2 className={CARD_TITLE}>契約情報</h2>
          {tenantStatus === 'loading' ? (
            <p role="status" className={`mt-4 ${NOTE}`}>
              読み込み中…
            </p>
          ) : tenant ? (
            <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
              <SummaryItem label="テナント名">{brand.brandName}</SummaryItem>
              <SummaryItem label="契約状態">
                <Chip tone={tenantStatusTone(tenant.status)}>
                  {TENANT_STATUS_LABELS[tenant.status]}
                </Chip>
              </SummaryItem>
              <SummaryItem label="契約開始日">
                <span className="ui-num tabular-nums">
                  {tenant.contractStartedAt.slice(0, 10)}
                </span>
              </SummaryItem>
              <SummaryItem label="契約中の機能">
                {features.length === 0 ? (
                  <span className="text-ink-2">
                    契約中の機能がありません（追加は SaaS 運営者へお問い合わせください）
                  </span>
                ) : (
                  <span className="flex flex-wrap gap-1.5">
                    {features.map((f) => (
                      <Chip key={f} tone="neutral">
                        {FEATURE_LABELS[f]}
                      </Chip>
                    ))}
                  </span>
                )}
              </SummaryItem>
            </dl>
          ) : (
            <p className={`mt-4 ${NOTE}`}>契約情報を取得できませんでした。</p>
          )}
        </article>
      </section>

      {canViewBilling && (
        <section aria-label="お支払い" className="mt-4">
          <article className={CARD}>
            <div className="flex items-start justify-between gap-3">
              <h2 className={CARD_TITLE}>お支払い</h2>
              <Link
                to={ROUTES.billing}
                className="inline-flex shrink-0 items-center justify-center rounded-lg bg-brand px-4 py-1.5 text-label font-medium text-white hover:opacity-90"
              >
                お支払い設定を開く
              </Link>
            </div>

            {/* PR #467 /review: tenant doc の読込中 / 取得失敗時に「未開始」「未登録」を
                確定情報のように出さない（契約情報カードと同じ分岐を適用）。 */}
            {tenantStatus === 'loading' ? (
              <p role="status" className={`mt-4 ${NOTE}`}>
                読み込み中…
              </p>
            ) : !tenant ? (
              <p className={`mt-4 ${NOTE}`}>お支払い情報を取得できませんでした。</p>
            ) : (
              <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                <SummaryItem label="支払い状態">
                  <Chip tone={billingStatusTone(billingStatus)}>
                    {BILLING_STATUS_LABELS[billingStatus]}
                  </Chip>
                </SummaryItem>
                <SummaryItem label="登録カード">
                  {tenant.payjpDefaultCard ? (
                    <>
                      {formatCardLabel(tenant.payjpDefaultCard)}
                      <span className="ml-1 text-label text-ink-3">
                        （有効期限 {formatCardExpiry(tenant.payjpDefaultCard)}）
                      </span>
                    </>
                  ) : (
                    <span className="text-ink-2">未登録</span>
                  )}
                </SummaryItem>
              </dl>
            )}

            <h3 className="mt-6 text-em font-semibold text-ink">請求履歴（直近）</h3>
            {chargesStatus === 'loading' ? (
              <p role="status" className={`mt-2 ${NOTE}`}>
                読み込み中…
              </p>
            ) : chargesStatus === 'error' ? (
              <p role="alert" className="mt-2 text-body text-danger-ink">
                請求履歴を取得できませんでした。
              </p>
            ) : charges.length === 0 ? (
              <p className={`mt-2 ${NOTE}`}>請求履歴はまだありません。</p>
            ) : (
              <ZebraTable className="mt-3" aria-label="請求履歴（直近）">
                <thead>
                  <ZebraTable.HeadRow>
                    <ZebraTable.Th edge="first">日付</ZebraTable.Th>
                    <ZebraTable.Th>内容</ZebraTable.Th>
                    <ZebraTable.Th align="right">金額</ZebraTable.Th>
                    <ZebraTable.Th edge="last">結果</ZebraTable.Th>
                  </ZebraTable.HeadRow>
                </thead>
                <tbody>
                  {charges.map((c) => (
                    <ZebraTable.Row key={c.id}>
                      <ZebraTable.Td numeric align="left" className="whitespace-nowrap">
                        {c.createdAt.slice(0, 10)}
                      </ZebraTable.Td>
                      <ZebraTable.Td>{c.description ?? billingChargeKindLabel(c.kind)}</ZebraTable.Td>
                      <ZebraTable.Td numeric className="whitespace-nowrap">
                        {formatYen(c.amountYen)}
                      </ZebraTable.Td>
                      <ZebraTable.Td>{chargeStatusLabel(c)}</ZebraTable.Td>
                    </ZebraTable.Row>
                  ))}
                </tbody>
              </ZebraTable>
            )}
          </article>
        </section>
      )}

      {/* S-75: 2 段階認証（任意 / opt-in）。MFA はアカウント単位のため、ここで登録すれば
          全管理 app のサインインで challenge が有効になる。impersonation 中は表示しない。 */}
      {!impersonatedBy && user && (
        <section aria-label="セキュリティ" className="mt-4 max-w-md">
          <MfaSettings
            variant="v2"
            user={user}
            enrolled={mfaEnrolled}
            issuer={MFA_ISSUER}
            accountName={email ?? 'admin'}
            onChange={refreshClaims}
          />
        </section>
      )}
    </div>
  );
}
